/**
 * supplierService.js
 * Supplier auto-detection and history management.
 * Modular — swap with external supplier DB independently.
 */

import hosys from "@/api/api";
import { similarity } from "./fuzzyService";

const STORAGE_KEY = "invoice_supplier_history";

// Local history (persisted to localStorage)
export function getSupplierHistory() {
  try {
    return JSON.parse(sessionStorage.getItem(STORAGE_KEY) || "[]");
  } catch {
    return [];
  }
}

export function saveSupplierToHistory(supplier) {
  if (!supplier?.name) return;
  const history = getSupplierHistory();
  const existing = history.findIndex(s => s.name === supplier.name);
  if (existing >= 0) {
    history[existing] = { ...history[existing], ...supplier, count: (history[existing].count || 1) + 1, lastSeen: new Date().toISOString() };
  } else {
    history.unshift({ ...supplier, count: 1, lastSeen: new Date().toISOString() });
  }
  // Keep top 50
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(history.slice(0, 50)));
}

// Match detected supplier name against history
export function matchSupplierFromHistory(detectedName) {
  if (!detectedName) return null;
  const history = getSupplierHistory();
  let best = null, bestScore = 0;
  for (const s of history) {
    const score = similarity(detectedName, s.name);
    if (score > bestScore) { bestScore = score; best = s; }
  }
  if (bestScore >= 0.6) return { supplier: best, confidence: Math.round(bestScore * 100) };
  return null;
}

// Get unique suppliers from past invoices for a venue
export async function getVenueSupplierHistory(venueId) {
  if (!venueId) return [];
  try {
    const invoices = await hosys.entities.Invoice.filter({ venue_id: venueId }, "-created_date", 100);
    const supplierMap = {};
    for (const inv of invoices) {
      if (!inv.supplier_name) continue;
      const key = inv.supplier_name;
      if (!supplierMap[key]) {
        supplierMap[key] = { name: inv.supplier_name, nif: inv.supplier_nif, count: 0, lastInvoice: null };
      }
      supplierMap[key].count++;
      if (!supplierMap[key].lastInvoice || inv.invoice_date > supplierMap[key].lastInvoice) {
        supplierMap[key].lastInvoice = inv.invoice_date;
      }
    }
    return Object.values(supplierMap).sort((a, b) => b.count - a.count);
  } catch {
    return [];
  }
}